import React from "react";
import { formatAppDateTime } from "../../../app/utils/appTimeFormat";
import { formatCompact } from "../../utils/formatCompact";
import {
  formatP2PCreatedAt,
  formatP2PKickoff,
  p2pOutcomeMeta,
  p2pStatusMeta,
} from "../../users/userP2PHelpers";
import P2PDuelMatchup from "./P2PDuelMatchup";
import P2PParticipantsPanel from "./P2PParticipantsPanel";

function DetailStat({ label, value }) {
  return (
    <div className="amf-desk-stat">
      <span className="amf-desk-stat-val">{value}</span>
      <span className="amf-desk-stat-key">{label}</span>
    </div>
  );
}

function formatKickoffFull(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return formatAppDateTime(d);
}

export default function P2PFloorDetail({ duel }) {
  if (!duel) {
    return (
      <section className="amf-detail amf-detail-idle ap2p-detail" aria-label="Select a duel">
        <div className="amf-idle">
          <p className="amf-idle-eyebrow">P2P desk</p>
          <p className="amf-idle-title">Select a duel from the ledger</p>
          <p className="amf-idle-copy">
            Review match, budget, and participants for each peer-to-peer duel.
          </p>
        </div>
      </section>
    );
  }

  const status = p2pStatusMeta(duel.status);
  const outcome = p2pOutcomeMeta(duel.p2pStatus);
  const showOutcome =
    duel.status === "Settled" || duel.p2pStatus === "Won" || duel.p2pStatus === "Lost";
  const joined = Number(duel.joinedPeople) || 0;
  const kickoffShort = formatP2PKickoff(duel.fixtureDate);
  const minStake = Number(duel.minStake ?? duel.perSeatStake ?? duel.stake) || 0;
  const budget = Number(duel.creatorBudget ?? duel.poolTotal) || 0;
  const remaining = Number(duel.ownerPendingAmount) || 0;

  return (
    <section className="amf-detail ap2p-detail" aria-label="Duel detail">
      <header className="amf-desk amf-desk-with-question ap2p-desk-header">
        <div className="ap2p-desk-fixture">
          <p className="ap2p-match-label">Match</p>
          <P2PDuelMatchup duel={duel} as="h2" scalableLogos />
        </div>

        <div className="amf-desk-stats ap2p-desk-stats">
          <DetailStat label="Budget" value={formatCompact(budget)} />
          <DetailStat label="Min stake" value={formatCompact(minStake)} />
          <DetailStat
            label="Counters"
            value={`${joined} · ${formatCompact(remaining)} left`}
          />
        </div>
      </header>

      <div className="amf-detail-scroll ap2p-detail-scroll">
        <div className="ap2p-detail-timing">
          <div className="ap2p-detail-time">
            <span className="ap2p-detail-time-key">Kickoff</span>
            <span className="ap2p-detail-time-val">
              {kickoffShort ? formatKickoffFull(duel.fixtureDate) : "—"}
            </span>
          </div>
          <div className="ap2p-detail-time">
            <span className="ap2p-detail-time-key">Created</span>
            <span className="ap2p-detail-time-val">{formatP2PCreatedAt(duel.createdAt)}</span>
          </div>
          {duel.leagueName ? (
            <div className="ap2p-detail-time">
              <span className="ap2p-detail-time-key">League</span>
              <span className="ap2p-detail-time-val">{duel.leagueName}</span>
            </div>
          ) : null}
          <div className="ap2p-detail-time">
            <span className="ap2p-detail-time-key">Duel status</span>
            <span className={`ap2p-detail-time-val ap2p-detail-time-val-${status.tone}`}>
              {status.label}
            </span>
          </div>
          {showOutcome ? (
            <div className="ap2p-detail-time">
              <span className="ap2p-detail-time-key">Outcome</span>
              <span className={`ap2p-detail-time-val ap2p-detail-time-val-${outcome.tone}`}>
                {outcome.label}
              </span>
            </div>
          ) : null}
        </div>

        {duel.selection || duel.creatorName ? (
          <section className="ap2p-panel ap2p-panel-pick">
            <h3 className="ap2p-panel-title">Creator backs</h3>
            {duel.selection ? <p className="ap2p-pick-value">{duel.selection}</p> : null}
            {duel.creatorName ? (
              <p className="ap2p-pick-creator">
                {duel.selection ? "By " : ""}
                {duel.creatorName}
              </p>
            ) : null}
          </section>
        ) : null}

        <P2PParticipantsPanel duel={duel} />
      </div>
    </section>
  );
}
